/**
 * Sim-lab ambient audio engine — procedural Web Audio (no asset files).
 * Docs: docs/SIM_LAB_AMBIENT_AUDIO.md
 *
 * Singleton graph:
 *   [track gain]* → master gain → duck gain → muffle low-pass → destination
 *
 * Tracks are synthesized (noise + filters + LFOs; gulls are scheduled
 * oscillator cries). Volumes are 0..1 and ramped to avoid clicks.
 */

const RAMP_S = 0.25;
const MUFFLE_OPEN_HZ = 16000;
const MUFFLE_CLOSED_HZ = 850;
const DUCK_INDOOR = 0.45;

let ctx = null;
let masterGain = null;
let duckGain = null;
let muffleFilter = null;
let enabled = false;
let masterVolume = 0.8;
let indoor = false;
/** @type {Map<string, { gain: GainNode, stop: () => void }>} */
const tracks = new Map();
/** Desired 0..1 per track (kept even while disabled). */
const trackVolumes = new Map();

function now() {
  return ctx ? ctx.currentTime : 0;
}

function ramp(param, value, seconds = RAMP_S) {
  try {
    param.cancelScheduledValues(now());
    param.setTargetAtTime(value, now(), Math.max(0.01, seconds / 3));
  } catch {
    param.value = value;
  }
}

function ensureContext() {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  ctx = new Ctx();

  masterGain = ctx.createGain();
  masterGain.gain.value = 0;
  duckGain = ctx.createGain();
  duckGain.gain.value = 1;
  muffleFilter = ctx.createBiquadFilter();
  muffleFilter.type = 'lowpass';
  muffleFilter.frequency.value = MUFFLE_OPEN_HZ;
  muffleFilter.Q.value = 0.4;

  masterGain.connect(duckGain);
  duckGain.connect(muffleFilter);
  muffleFilter.connect(ctx.destination);
  return ctx;
}

/** 4 s noise buffer (new random each call — loops are seamless enough). */
function makeNoiseBuffer() {
  const len = ctx.sampleRate * 4;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  // Paul Kellet-style pink-ish noise for a softer bed than white noise.
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  for (let i = 0; i < len; i += 1) {
    const white = Math.random() * 2 - 1;
    b0 = 0.997 * b0 + 0.029591 * white;
    b1 = 0.985 * b1 + 0.032534 * white;
    b2 = 0.95 * b2 + 0.048056 * white;
    data[i] = (b0 + b1 + b2 + white * 0.05) * 2.1;
  }
  return buf;
}

function makeLoopSource() {
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer();
  src.loop = true;
  return src;
}

/** LFO oscillator → gain.gain, oscillating around `base` with `depth`. */
function attachGainLfo(gainNode, hz, base, depth, phaseOffsetS = 0) {
  gainNode.gain.value = base;
  const lfo = ctx.createOscillator();
  lfo.frequency.value = hz;
  const lfoAmp = ctx.createGain();
  lfoAmp.gain.value = depth;
  lfo.connect(lfoAmp);
  lfoAmp.connect(gainNode.gain);
  lfo.start(now() + phaseOffsetS);
  return lfo;
}

/* ————— Track builders — recipes in docs/SIM_LAB_AMBIENT_AUDIO.md ————— */

function buildSurf(outGain) {
  const stops = [];

  // Swell layer — deep rumble rising and falling slowly.
  const swellSrc = makeLoopSource();
  const swellLp = ctx.createBiquadFilter();
  swellLp.type = 'lowpass';
  swellLp.frequency.value = 480;
  const swellGain = ctx.createGain();
  const swellLfo = attachGainLfo(swellGain, 0.08, 0.5, 0.3);
  swellSrc.connect(swellLp);
  swellLp.connect(swellGain);
  swellGain.connect(outGain);
  swellSrc.start();
  stops.push(() => {
    swellSrc.stop();
    swellLfo.stop();
  });

  // Swash layer — brighter wash on a faster, phase-offset cycle.
  const swashSrc = makeLoopSource();
  const swashLp = ctx.createBiquadFilter();
  swashLp.type = 'lowpass';
  swashLp.frequency.value = 1400;
  const swashGain = ctx.createGain();
  const swashLfo = attachGainLfo(swashGain, 0.13, 0.22, 0.16, 2.4);
  swashSrc.connect(swashLp);
  swashLp.connect(swashGain);
  swashGain.connect(outGain);
  swashSrc.start();
  stops.push(() => {
    swashSrc.stop();
    swashLfo.stop();
  });

  return () => stops.forEach((s) => s());
}

function buildWind(outGain) {
  const src = makeLoopSource();
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 420;
  bp.Q.value = 0.5;
  const bed = ctx.createGain();
  const lfo = attachGainLfo(bed, 0.05, 0.55, 0.2);
  src.connect(bp);
  bp.connect(bed);
  bed.connect(outGain);
  src.start();

  // Random gust targets on top of the LFO drift.
  let gustTimer = null;
  const gust = () => {
    ramp(bp.frequency, 320 + Math.random() * 260, 2.5);
    gustTimer = window.setTimeout(gust, 4000 + Math.random() * 6000);
  };
  gust();

  return () => {
    src.stop();
    lfo.stop();
    if (gustTimer) window.clearTimeout(gustTimer);
  };
}

function buildGulls(outGain) {
  let cancelled = false;
  let timer = null;

  const cry = (when, baseHz) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    const vib = ctx.createOscillator();
    vib.frequency.value = 26;
    const vibAmp = ctx.createGain();
    vibAmp.gain.value = 40;
    vib.connect(vibAmp);
    vibAmp.connect(osc.frequency);

    const env = ctx.createGain();
    env.gain.value = 0;
    osc.connect(env);
    env.connect(outGain);

    const dur = 0.32 + Math.random() * 0.22;
    osc.frequency.setValueAtTime(baseHz, when);
    osc.frequency.exponentialRampToValueAtTime(baseHz * 0.58, when + dur);
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(0.16, when + 0.05);
    env.gain.exponentialRampToValueAtTime(0.001, when + dur);

    osc.start(when);
    vib.start(when);
    osc.stop(when + dur + 0.05);
    vib.stop(when + dur + 0.05);
  };

  const cluster = () => {
    if (cancelled) return;
    const cries = 1 + Math.floor(Math.random() * 3);
    const base = 1100 + Math.random() * 320;
    for (let i = 0; i < cries; i += 1) {
      cry(now() + i * (0.45 + Math.random() * 0.25), base * (1 + i * 0.04));
    }
    timer = window.setTimeout(cluster, 14000 + Math.random() * 20000);
  };
  timer = window.setTimeout(cluster, 3000 + Math.random() * 6000);

  return () => {
    cancelled = true;
    if (timer) window.clearTimeout(timer);
  };
}

const TRACK_BUILDERS = { surf: buildSurf, wind: buildWind, gulls: buildGulls };

function ensureTrack(id) {
  if (!ctx || tracks.has(id) || !TRACK_BUILDERS[id]) return;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(masterGain);
  const stop = TRACK_BUILDERS[id](gain);
  tracks.set(id, { gain, stop });
}

/* ————— Public API ————— */

export function ambientSetEnabled(on) {
  enabled = Boolean(on);
  if (enabled) {
    if (!ensureContext()) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    trackVolumes.forEach((vol, id) => {
      ensureTrack(id);
      const t = tracks.get(id);
      if (t) ramp(t.gain.gain, vol);
    });
    ramp(masterGain.gain, masterVolume, 0.6);
  } else if (ctx) {
    ramp(masterGain.gain, 0, 0.4);
  }
}

export function ambientSetMasterVolume(v) {
  masterVolume = Math.min(1, Math.max(0, Number(v) || 0));
  if (ctx && enabled) ramp(masterGain.gain, masterVolume);
}

export function ambientSetTrackVolume(id, v) {
  const vol = Math.min(1, Math.max(0, Number(v) || 0));
  trackVolumes.set(id, vol);
  if (!ctx || !enabled) return;
  ensureTrack(id);
  const t = tracks.get(id);
  if (t) ramp(t.gain.gain, vol);
}

/** Apply preset track map ({ surf: 0.7, ... }). */
export function ambientApplyTracks(map) {
  Object.entries(map || {}).forEach(([id, v]) => ambientSetTrackVolume(id, v));
}

/** Indoor muffle — low-pass + duck (walk mode inside footprint). */
export function ambientSetIndoor(inside) {
  const next = Boolean(inside);
  if (next === indoor) return;
  indoor = next;
  if (!ctx) return;
  ramp(muffleFilter.frequency, indoor ? MUFFLE_CLOSED_HZ : MUFFLE_OPEN_HZ, 0.4);
  ramp(duckGain.gain, indoor ? DUCK_INDOOR : 1, 0.4);
}

/** Full teardown (page unmount). */
export function ambientDispose() {
  tracks.forEach((t) => {
    try {
      t.stop();
    } catch {
      /* already stopped */
    }
  });
  tracks.clear();
  if (ctx) {
    ctx.close().catch(() => {});
  }
  ctx = null;
  masterGain = null;
  duckGain = null;
  muffleFilter = null;
  enabled = false;
  indoor = false;
}
